/**
 * Which role controls to show on the Members screen.
 *
 * The authority is the server: `canAssignRole` in
 * `apps/api/src/organizations/member-role-policy.ts`. This mirrors it so the UI
 * does not offer a choice that is going to come back 403 — but it is a
 * convenience, not the gate. If the two ever disagree, the server wins and the
 * user sees its message.
 *
 * Two rules worth restating, because they look like omissions otherwise:
 *
 *   - **Nobody edits their own row.** An owner demoting themselves is the
 *     fastest way to an organization nobody can administer. Stepping down means
 *     promoting someone else, who then demotes you.
 *   - **A role the dropdown cannot assign is not editable.** It offers only the
 *     three real roles, so a membership left on a legacy value — the removed
 *     boat-owner role, say — shows as a badge. The server would accept the
 *     change as cleanup; nothing in the UI offers it, because nothing should
 *     create one again.
 *
 * Super admins are handled by the caller, which passes `owner` for them — that
 * is what `TenantGuard` resolves them to on any organization.
 */

/** Highest first, which is also the order the dropdown shows. */
const ROLE_RANK = ['owner', 'admin', 'member'] as const;

export type AssignableRole = (typeof ROLE_RANK)[number];

export interface RoleEditContext {
  /** The viewer's role in this organization (`owner` for a super admin). */
  viewerRole: string | null | undefined;
  /** The role of the row being rendered. */
  targetRole: string;
  isSelf: boolean;
}

/** The roles this viewer may hand out. Empty means: show no control. */
export function assignableRoles(viewerRole: string | null | undefined): AssignableRole[] {
  if (viewerRole === 'owner') return [...ROLE_RANK];
  // Only an owner grants or revokes `owner`, so offering it to an admin would
  // be an option that always fails.
  if (viewerRole === 'admin') return ['admin', 'member'];
  return [];
}

export function isRoleEditable({ viewerRole, targetRole, isSelf }: RoleEditContext): boolean {
  if (isSelf) return false;

  const options = assignableRoles(viewerRole);
  if (options.length === 0) return false;

  // An admin cannot revoke the role above them.
  return options.includes(targetRole as AssignableRole);
}
